import {
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { TextInput, Pressable, ViewStyle } from 'react-native';
import { FlatSelectOption, FlatSelectProps } from './FlatSelect.types';
import Input from '../Input/Input';
import { throttle } from 'lodash-es';
import Box from '../Box/Box';
import Text from '../Text/Text';
import List from '../List/List';
import { Styles } from '../../index';

function FlatSelect(
  {
    value,
    options,
    hasSearch = false,
    minSearchLength = 0,
    columns = 2,
    maxHeight = 300,
    centerContent = true,
    scrollEnabled = true,
    headerNS,
    headerKey,
    placeholderNS,
    placeholderKey,
    onValue,
    onSearchChange,
    useDynamicOptions = false,
    error,
  }: FlatSelectProps,
  ref: ForwardedRef<TextInput>,
) {
  const [searchValue, setSearchValue] = useState(value || '');
  const [filteredOptions, setFilteredOptions] = useState(
    minSearchLength ? [] : options,
  );
  const defaultSelectedOptionId = hasSearch
    ? (options.find((option) => option.label === value)?.id ?? '')
    : (value ?? '');
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(
    defaultSelectedOptionId || null,
  );

  const columnWrapperStyle: Styles<ViewStyle> | undefined =
    columns > 1
      ? {
          gap: 'space-200',
        }
      : undefined;

  const handleInputChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      if (selectedOptionId) {
        setSelectedOptionId(null);
      }

      if (useDynamicOptions) {
        onSearchChange?.(value);
      } else {
        onValue?.(value);
      }
    },
    [onValue, onSearchChange, useDynamicOptions, selectedOptionId],
  );

  const filterOptions = useCallback(
    throttle((searchValue: string, options: FlatSelectProps['options']) => {
      const regex = new RegExp(searchValue, 'gi');
      const filteredOptions = options.filter((option) =>
        option.label.match(regex),
      );

      setFilteredOptions(filteredOptions);
    }, 500),
    [],
  );

  useEffect(() => {
    if (useDynamicOptions) {
      if (hasSearch && searchValue.length === 0) {
        setFilteredOptions([]);
      } else {
        setFilteredOptions(options);
      }
    } else {
      if (hasSearch) {
        if (searchValue.length === 0) {
          filterOptions.cancel();
          setFilteredOptions([]);
        } else if (searchValue.length >= minSearchLength) {
          filterOptions(searchValue, options);
        } else {
          filterOptions.cancel();
          setFilteredOptions([]);
        }
      } else {
        setFilteredOptions(minSearchLength ? [] : options);
      }
    }
  }, [
    searchValue,
    minSearchLength,
    options,
    filterOptions,
    hasSearch,
    useDynamicOptions,
  ]);

  const handleOptionPress = useCallback(
    (optionId: string) => {
      if (selectedOptionId === optionId) {
        setSelectedOptionId(null);
        onValue?.('');
      } else {
        setSelectedOptionId(optionId);
        if (hasSearch) {
          const selectedLabel = options.find(
            (option) => option.id === optionId,
          )?.label;
          onValue?.(selectedLabel || optionId);
        } else {
          onValue?.(optionId);
        }
      }
    },
    [selectedOptionId, onValue, hasSearch, options],
  );

  const renderItem = useCallback(
    ({ item }: { item: FlatSelectOption }) => {
      const isSelected = item.id === selectedOptionId;

      return (
        <Box flex={1}>
          <Pressable onPress={() => handleOptionPress(item.id)}>
            <Box
              padding="space-300"
              borderBottomWidth="border-width-025"
              borderRadius="radius-200"
              marginBottom="space-200"
              centerContent={centerContent}
              {...(hasSearch &&
                columns > 1 && {
                  height: {
                    mobile: 'space-1600',
                    tablet: 'space-1600',
                    desktop: undefined,
                  },
                })}
              {...(isSelected
                ? {
                    variant: 'gradient',
                    gradient: 'accentBackground',
                    borderColor: 'borderSecondary',
                  }
                : {
                    variant: 'default',
                    backgroundColor: 'mutedBackground',
                  })}
            >
              <Text
                variant="body-s"
                color={
                  isSelected ? 'accentSurfacePrimary' : 'darkSurfacePrimary'
                }
                numberOfLines={hasSearch && columns > 1 ? 2 : 1}
                textAlign={hasSearch && columns > 1 ? 'center' : undefined}
              >
                {item.label}
              </Text>
            </Box>
          </Pressable>
        </Box>
      );
    },
    [selectedOptionId, centerContent, handleOptionPress, hasSearch, columns],
  );

  useEffect(() => {
    if (value !== undefined) {
      setSearchValue(value);
    }
  }, [value]);

  return (
    <Box>
      {headerNS && headerKey && (
        <Text
          color="darkSurfacePrimary"
          marginBottom="space-300"
          variant="h5"
          locNS={headerNS}
          locKey={headerKey}
        />
      )}
      {placeholderNS && placeholderKey && (
        <Text
          color="darkSurfaceSecondary"
          marginBottom="space-400"
          variant="body-s"
          locNS={placeholderNS}
          locKey={placeholderKey}
        />
      )}
      {hasSearch ? (
        <Input
          ref={ref}
          value={searchValue}
          icon="search"
          onChangeText={handleInputChange}
          placeholderNS={placeholderNS}
          placeholderKey={placeholderKey}
          marginBottom="space-200"
        />
      ) : null}
      <Box maxHeight={maxHeight}>
        <List
          scrollEnabled={scrollEnabled}
          data={filteredOptions}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          showsVerticalScrollIndicator={false}
          numColumns={columns}
          columnWrapperStyle={columnWrapperStyle}
          nestedScrollEnabled={true}
          keyboardShouldPersistTaps="handled"
        />
      </Box>
      {!hasSearch && error && (
        <Text color="darkSurfaceError" variant="body-m">
          {error}
        </Text>
      )}
    </Box>
  );
}

export default memo(forwardRef(FlatSelect));
